import { PayloadAction } from "@reduxjs/toolkit";
import { takeLatest, all, put, call } from "redux-saga/effects";

import { commonMessages, ILocale, isDefaultLocale } from "infrastructure/localization";
import { notification } from "infrastructure/notification";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";
import { applyPickerFilter, IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { termDictionaryActions } from "./TermDictionary.actions";
import { termDictionaryApi as api } from "./TermDictionary.api";
import { createSearchResult } from "./TermDictionary.helpers";
import { ITermDictionarySearchQuery, IIndustryPickerSearchCriterion } from "./TermDictionary.state";

function* searchTermTranslationsExecute({ payload }: PayloadAction<ITermDictionarySearchQuery>) {
  try {
    // TODO: remove validation logic marked with 'istanbul ignore next' when we have validation on component level
    /* istanbul ignore next */
    if (payload.locale) {
      /* istanbul ignore next */
      if (isDefaultLocale(payload.locale.localeIdentifier) && payload.bilingual && payload.industry === undefined) {
        yield put(termDictionaryActions.searchTermTranslationsFailed(notification.warning().useMessageId(glossaryMessages.error("industryRequired"))));
      } else {
        const translations = yield call(api.getTranslations, payload);
        yield put(termDictionaryActions.searchTermTranslationsCompleted(createSearchResult(translations, payload)));
      }
    } else {
      /* istanbul ignore next */
      yield put(termDictionaryActions.searchTermTranslationsFailed(notification.warning().useMessageId(glossaryMessages.error("localeRequired"))));
    }
  } catch (e) {
    yield put(termDictionaryActions.searchTermTranslationsFailed(notification.error(e).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* searchIndustriesExecute({ payload }: PayloadAction<IIndustryPickerSearchCriterion>) {
  try {
    const industries = yield call(api.getIndustries, payload.localeIdentifier, payload.query.query);
    yield put(termDictionaryActions.searchIndustriesCompleted(applyPickerFilter(industries, payload, row => row.description)));
  } catch (e) {
    yield put(termDictionaryActions.searchIndustriesFailed(notification.error(e).useMessageId(glossaryMessages.error("loadIndustriesFailed"))));
  }
}

function* searchLocalesExecute({ payload }: PayloadAction<IPickerSearchCriterion<ILocale>>) {
  try {
    const locales = yield call(api.getLocales, payload.query.query);

    yield put(termDictionaryActions.searchLocalesCompleted({ locales: applyPickerFilter(locales, payload, row => row.name), searchCriterion: payload }));
  } catch (e) {
    yield put(termDictionaryActions.searchLocalesFailed(notification.error(e).useMessageId(commonMessages.error("loadLocalesFailed"))));
  }
}

function* exportDictionaryTermTranslationsExecute({ payload }: PayloadAction<ITermDictionarySearchQuery>) {
  try {
    yield call(api.exportDictionaryTermTranslations, payload);
  } catch (e) {
    yield put(termDictionaryActions.exportTermTranslationsFailed(notification.error(e).useMessageId(glossaryMessages.error("exportFailed"))));
  }
}

function* actionWatcher() {
  yield takeLatest(termDictionaryActions.searchTermTranslations.toString(), searchTermTranslationsExecute);
  yield takeLatest(termDictionaryActions.searchIndustries.toString(), searchIndustriesExecute);
  yield takeLatest(termDictionaryActions.searchLocales.toString(), searchLocalesExecute);
  yield takeLatest(termDictionaryActions.exportTermTranslations.toString(), exportDictionaryTermTranslationsExecute);
}

export function* termDictionarySaga() {
  yield all([actionWatcher()]);
}
